import { useCallback, useEffect, useRef, useState } from "react";
import { Duration, LocalDateTime } from "@js-joda/core";
import { Alert, Modal } from "react-bootstrap";
import { Button } from "../../Controls/Button";
import { HeaderText } from "../../Controls/HeaderText";
import { createApiService } from "../../shared/apiConfig";
import {
  PlateScanApi,
  ServerRequestsMonthlyPlateMatch,
  ServerRequestsPlateScanResponse,
} from "../../shared/api/axios-client";
import { toast } from "react-toastify";
import { Camera, CheckCircle, Clock, DashCircle, ExclamationCircle, Pencil, XCircle } from "react-bootstrap-icons";
import { MonthlyPlateMatchList } from "./MonthlyPlateMatchList";

const VIDEO_CONSTRAINTS: MediaStreamConstraints = {
  video: {
    facingMode: "environment",
    width: { ideal: 1280 },
    height: { ideal: 720 },
  },
};

const CROP_RATIO = 0.7;
const JPEG_QUALITY = 0.92;

export interface PlateSessionInfo {
  status: "active" | "expired" | "upcoming" | "not_found";
  /** Pre-formatted start date/time string, e.g. "Mar 31, 9:00 AM" */
  from?: string;
  /** Pre-formatted end date/time string, e.g. "Mar 31, 5:00 PM" */
  to?: string;
  start?: LocalDateTime;
  end?: LocalDateTime;
}

export interface PlateScannerModalProps {
  show: boolean;
  onHide: () => void;
  facilityId?: number;
  /** Return session info for the scanned plate, or null if not found. */
  getPlateSession?: (plate: string) => PlateSessionInfo | null;
}

type Stage = "camera" | "scanning" | "result" | "error";

function timeLeftLabel(end: LocalDateTime, now: LocalDateTime): string {
  const mins = Duration.between(now, end).toMinutes();
  if (mins <= 0) return "Ended";
  if (mins < 1) return "<1m left";
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  if (hours <= 0) return `${minutes}m left`;
  if (minutes === 0) return `${hours}h left`;
  return `${hours}h ${minutes}m left`;
}

function sessionProgress(start: LocalDateTime, end: LocalDateTime, now: LocalDateTime): number {
  const total = Duration.between(start, end).toMillis();
  if (total <= 0) return 1;
  const elapsed = Duration.between(start, now).toMillis();
  return Math.min(1, Math.max(0, elapsed / total));
}

function plateStateLabel(region?: string | null, regionName?: string | null): string | null {
  const raw = (region || "").trim();
  if (raw) {
    const parts = raw.split("-");
    const code = (parts.length > 1 ? parts[parts.length - 1] : raw).trim();
    if (code && code.length <= 3) return code.toUpperCase();
  }
  const name = regionName?.trim();
  return name || null;
}

const sessionResultTheme: Record<PlateSessionInfo["status"], { bg: string; color: string; border: string; icon: React.ReactNode; label: string }> = {
  active:    { bg: "#e9f6df", color: "#2f7d32", border: "#d4ebc4", icon: <CheckCircle size={22} />,       label: "Active session" },
  expired:   { bg: "#f8e8ea", color: "#9f2d2d", border: "#eed2d6", icon: <XCircle size={22} />,           label: "Session expired" },
  upcoming:  { bg: "#dbeafe", color: "#1f5fae", border: "#c7dcf7", icon: <Clock size={22} />,             label: "Upcoming session" },
  not_found: { bg: "#f8efd9", color: "#7a5a18", border: "#eadfbd", icon: <ExclamationCircle size={22} />, label: "No session on file" },
};

export function PlateScannerModal({
  show,
  onHide,
  facilityId,
  getPlateSession,
}: PlateScannerModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [stage, setStage] = useState<Stage>("camera");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [result, setResult] = useState<ServerRequestsPlateScanResponse | null>(null);
  const [monthlyMatches, setMonthlyMatches] = useState<ServerRequestsMonthlyPlateMatch[]>([]);
  const [plateOverride, setPlateOverride] = useState<string | null>(null);
  const [editingPlate, setEditingPlate] = useState(false);
  const [plateDraft, setPlateDraft] = useState("");
  const plateScanApi = createApiService(PlateScanApi);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    stopCamera();
    setErrorMessage(null);
    setStage("camera");
    setResult(null);
    setMonthlyMatches([]);
    setPlateOverride(null);
    setEditingPlate(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia(VIDEO_CONSTRAINTS);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Camera access was denied.";
      setErrorMessage(msg);
      setStage("error");
      toast.error("Could not access camera. Please allow camera permission.");
    }
  }, [stopCamera]);

  useEffect(() => {
    if (show) {
      startCamera();
    }
    return () => {
      stopCamera();
    };
  }, [show, startCamera, stopCamera]);

  const handleClose = useCallback(() => {
    stopCamera();
    setStage("camera");
    setResult(null);
    setMonthlyMatches([]);
    setPlateOverride(null);
    setEditingPlate(false);
    setErrorMessage(null);
    onHide();
  }, [onHide, stopCamera]);

  const captureAndScan = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !streamRef.current) return;

    setStage("scanning");
    setErrorMessage(null);

    try {
      const w = video.videoWidth;
      const h = video.videoHeight;
      const cropW = Math.floor(w * CROP_RATIO);
      const cropH = Math.floor(h * CROP_RATIO);
      const x = Math.floor((w - cropW) / 2);
      const y = Math.floor((h - cropH) / 2);

      const canvas = document.createElement("canvas");
      canvas.width = cropW;
      canvas.height = cropH;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas not available");
      ctx.drawImage(video, x, y, cropW, cropH, 0, 0, cropW, cropH);

      const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
      const base64 = dataUrl.replace(/^data:image\/jpeg;base64,/, "");

      const response = await plateScanApi.scanPlate({
        serverRequestsPlateScanRequest: { image: base64, regionHint: "us" },
      });

      const plate = (response.data?.plate ?? "").trim();
      let monthly: ServerRequestsMonthlyPlateMatch[] = [];
      if (plate && facilityId != null) {
        try {
          monthly = (await plateScanApi.lookupMonthlyPlate({ facilityId, plate })).data.matches ?? [];
        } catch {
          toast.error("Could not check monthly parking.");
        }
      }

      setPlateOverride(null);
      setEditingPlate(false);
      setMonthlyMatches(monthly);
      setResult(response.data ?? null);
      setStage("result");
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "message" in err
          ? String((err as { message: string }).message)
          : "Scan failed. Please try again.";
      setErrorMessage(msg);
      setStage("error");
      toast.error("Plate scan failed. Please try again.");
    }
  }, [plateScanApi, facilityId]);

  const handleRetry = useCallback(() => {
    setResult(null);
    setPlateOverride(null);
    setEditingPlate(false);
    setErrorMessage(null);
    startCamera();
  }, [startCamera]);

  const applyPlateEdit = useCallback(async () => {
    const next = plateDraft.trim();
    setEditingPlate(false);
    if (!next) return;
    setPlateOverride(next);
    if (facilityId == null) {
      setMonthlyMatches([]);
      return;
    }
    try {
      setMonthlyMatches((await plateScanApi.lookupMonthlyPlate({ facilityId, plate: next })).data.matches ?? []);
    } catch {
      toast.error("Could not check monthly parking.");
      setMonthlyMatches([]);
    }
  }, [facilityId, plateDraft, plateScanApi]);

  if (!show) return null;

  const shownPlate = (plateOverride ?? result?.plate ?? "").trim();
  const shownSession = shownPlate && getPlateSession ? getPlateSession(shownPlate) : null;
  const noMatch = stage === "result" && !!result
    && monthlyMatches.length === 0
    && (!shownSession || shownSession.status === "not_found");

  const scannerBrand = "#e91e8c";
  const cornerBase: React.CSSProperties = {
    position: "absolute",
    width: 22,
    height: 22,
    borderColor: scannerBrand,
    borderStyle: "solid",
    borderWidth: 0,
    pointerEvents: "none",
    zIndex: 2,
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <HeaderText>{stage !== "result" ? "Scan plate" : noMatch ? "Scan result" : "Plate lookup"}</HeaderText>
      </Modal.Header>
      <Modal.Body className="p-3">
        <style>{`
          @keyframes captureRipple {
            0%   { transform: translate(-50%, -50%) scale(1);   opacity: 0; }
            8%   { opacity: 0.18; }
            100% { transform: translate(-50%, -50%) scale(1.9); opacity: 0; }
          }
          .capture-btn-ripple::before,
          .capture-btn-ripple::after {
            content: "";
            position: absolute;
            width: 48px;
            height: 48px;
            left: 50%;
            top: 50%;
            border-radius: 50%;
            background: ${scannerBrand};
            animation: captureRipple 3s ease-out infinite;
          }
          .capture-btn-ripple::after {
            animation-delay: 1.5s;
            animation-fill-mode: backwards;
          }
          @keyframes resultFadeIn {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
          .result-fade-in {
            animation: resultFadeIn 0.5s ease-in-out forwards;
          }
        `}</style>

        {/* ── Camera stage ── */}
        {stage === "camera" && (
          <>
            <div className="position-relative rounded-3 overflow-hidden bg-dark mb-3">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-100"
                style={{ display: "block", maxHeight: "60vh", objectFit: "cover" }}
              />
              {/* Corner brackets */}
              <div style={{ ...cornerBase, top: 10, left: 10, borderTopWidth: 3, borderLeftWidth: 3 }} />
              <div style={{ ...cornerBase, top: 10, right: 10, borderTopWidth: 3, borderRightWidth: 3 }} />
              <div style={{ ...cornerBase, bottom: 10, left: 10, borderBottomWidth: 3, borderLeftWidth: 3 }} />
              <div style={{ ...cornerBase, bottom: 10, right: 10, borderBottomWidth: 3, borderRightWidth: 3 }} />
            </div>

            <p className="text-muted small mb-3 text-center">
              Position the license plate within the frame, then tap to capture
            </p>

            <div className="d-flex justify-content-center mt-2 mb-1">
              <button
                type="button"
                className="capture-btn-ripple border-0 bg-transparent p-0 position-relative d-flex align-items-center justify-content-center"
                style={{ width: 48, height: 48 }}
                onClick={captureAndScan}
                aria-label="Capture"
              >
                <span
                  className="position-relative rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: 48, height: 48, background: scannerBrand, zIndex: 1 }}
                >
                  <Camera size={20} color="#fff" />
                </span>
              </button>
            </div>
          </>
        )}

        {/* ── Scanning stage ── */}
        {stage === "scanning" && (
          <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 260 }}>
            <p className="text-muted mb-0">Reading plate...</p>
          </div>
        )}

        {/* ── Result stage ── */}
        {stage === "result" && result && (() => {
          const plateStr = shownPlate;
          const session = shownSession;
          const confidencePct = result.confidence != null ? Math.round(result.confidence * 100) : null;
          const confidenceLevel = confidencePct == null ? null : confidencePct >= 90 ? "High" : confidencePct >= 75 ? "Medium" : "Low";

          const hasMonthly = monthlyMatches.length > 0;
          const hasTransient = !!session && session.status !== "not_found";
          const status = hasTransient && session ? session.status : "not_found";
          const theme = sessionResultTheme[status];
          const sessionWindow = session?.from && session?.to ? `${session.from} – ${session.to}` : null;
          const statusMessage = status === "active"
            ? session?.to ? `Covered now - ends ${session.to}` : "Covered now"
            : status === "upcoming"
              ? sessionWindow ? `Starts later - ${sessionWindow}` : "Starts later"
              : status === "expired"
                ? session?.to ? `Expired - ended ${session.to}` : "Expired"
                : "No session or monthly plan";
          const stateLabel = plateStateLabel(result.region, result.regionName);
          const typeLabel = hasMonthly ? "Monthly parker" : hasTransient ? "Transient" : "Not authorized to park";
          const typeColor = hasMonthly ? "#157347" : hasTransient ? theme.color : "#8a5a12";
          const typeIcon = hasMonthly
            ? <CheckCircle size={22} />
            : hasTransient
              ? theme.icon
              : <ExclamationCircle size={22} />;
          const planCountLabel = monthlyMatches.length === 1
            ? "1 active plan includes this plate"
            : `${monthlyMatches.length} active plans include this plate`;

          const plateCard = (
            <div
              className="rounded-3 border bg-white text-center px-3 py-2"
              style={{ width: "100%" }}
            >
              {stateLabel && (
                <div className="text-muted fw-semibold" style={{ fontSize: "0.7rem", letterSpacing: "0.06em" }}>
                  [{stateLabel}]
                </div>
              )}
              {editingPlate ? (
                <input
                  autoFocus
                  value={plateDraft}
                  onChange={(e) => setPlateDraft(e.target.value)}
                  onBlur={() => void applyPlateEdit()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void applyPlateEdit();
                    if (e.key === "Escape") setEditingPlate(false);
                  }}
                  className="form-control form-control-sm text-center fw-bold border-0 p-0"
                  style={{ fontSize: "1.35rem", letterSpacing: "0.04em" }}
                  aria-label="Edit plate"
                />
              ) : (
                <div className="fw-bold" style={{ fontSize: "1.75rem", letterSpacing: "0.04em", lineHeight: 1.1 }}>
                  {plateStr || "—"}
                </div>
              )}
            </div>
          );

          if (!hasMonthly && !hasTransient) {
            return (
              <div className="result-fade-in">
                <div className="mb-3">{plateCard}</div>

                <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                  {plateOverride == null && confidencePct != null && confidenceLevel ? (
                    <span
                      className="rounded-pill px-2 py-1 fw-semibold"
                      style={{
                        background: confidenceLevel === "High" ? "#d9f5e5" : confidenceLevel === "Medium" ? "#fde8c8" : "#f8e8ea",
                        color: confidenceLevel === "High" ? "#157347" : confidenceLevel === "Medium" ? "#9a6700" : "#9f2d2d",
                        fontSize: "0.8rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {confidenceLevel} confidence · {confidencePct}%
                    </span>
                  ) : <span />}
                  <button
                    type="button"
                    className="btn btn-link text-decoration-none p-0 fw-semibold d-inline-flex align-items-center gap-1"
                    style={{ color: "#D61F84" }}
                    onClick={() => {
                      setPlateDraft(plateStr);
                      setEditingPlate(true);
                    }}
                  >
                    <Pencil size={14} />
                    Edit plate
                  </button>
                </div>

                <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-4" role="alert">
                  <span className="flex-shrink-0 mt-1">{typeIcon}</span>
                  <div style={{ minWidth: 0 }}>
                    <div className="fw-semibold" style={{ fontSize: "1.05rem" }}>{typeLabel}</div>
                    <div className="small mt-1">{statusMessage}</div>
                  </div>
                </div>

                <div className="text-muted fw-semibold mb-2" style={{ fontSize: "0.7rem", letterSpacing: "0.08em" }}>
                  WHAT WE CHECKED
                </div>
                <div className="d-flex flex-column gap-2 mb-3">
                  {[
                    ["Active parking sessions", "None"],
                    ["Monthly plans", "None"],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="d-flex align-items-center justify-content-between rounded-3 px-3 py-2"
                      style={{ border: "1px solid #e9ecef" }}
                    >
                      <div className="d-flex align-items-center gap-2 text-muted">
                        <DashCircle size={18} />
                        <span>{label}</span>
                      </div>
                      <span className="text-muted">{value}</span>
                    </div>
                  ))}
                </div>

                <div className="d-flex gap-2 mt-3">
                  <Button type="button" className="rounded-4 flex-fill" onClick={handleClose}>
                    Dismiss
                  </Button>
                  <Button type="button" className="rounded-4 btn-main flex-fill" onClick={handleRetry}>
                  
                    Scan again
                  </Button>
                </div>
              </div>
            );
          }

          const coveredLabel = hasTransient && status === "active" ? "Covered" : typeLabel;
          const coveredDetail = hasTransient && status === "active"
            ? "Transient session"
            : hasMonthly
              ? planCountLabel
              : statusMessage;
          const now = LocalDateTime.now();
          const showProgress = status === "active" && !!session?.start && !!session?.end;
          const progress = showProgress && session?.start && session?.end
            ? sessionProgress(session.start, session.end, now)
            : 0;
          const leftLabel = showProgress && session?.end ? timeLeftLabel(session.end, now) : null;

          return (
            <div className="result-fade-in">
              <div className="d-flex flex-column flex-sm-row align-items-center gap-3 mb-3 mt-2">
                <div className="flex-shrink-0" style={{ width: "100%", maxWidth: 200 }}>{plateCard}</div>
                <div className="text-center text-sm-start" style={{ minWidth: 0 }}>
                  <div className="d-flex align-items-center justify-content-center justify-content-sm-start gap-2 fw-semibold" style={{ color: hasTransient && status === "active" ? "#157347" : typeColor, fontSize: "1.15rem" }}>
                    {hasTransient && status === "active" ? <CheckCircle size={22} /> : typeIcon}
                    <span>{coveredLabel}</span>
                  </div>
                  <div className="text-muted small">{coveredDetail}</div>
                </div>
              </div>

              {showProgress && leftLabel && session?.from && session?.to && (
                <div className="rounded-3 px-3 py-3 mb-3" style={{ background: "#e7f6ee" }}>
                  <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
                    <div className="fw-bold" style={{ color: "#157347", fontSize: "1.15rem" }}>{leftLabel}</div>
                    <div className="text-muted small">Ends {session.to}</div>
                  </div>
                  <div className="rounded-pill overflow-hidden mb-2" style={{ height: 6, background: "#cfe8da" }}>
                    <div style={{ width: `${Math.round(progress * 100)}%`, height: "100%", background: "#1f9d55" }} />
                  </div>
                  <div className="d-flex justify-content-between text-muted small">
                    <span>{session.from}</span>
                    <span>{session.to}</span>
                  </div>
                </div>
              )}

              {sessionWindow && (
                <div className="rounded-3 mb-3" style={{ border: "1px solid #e9ecef" }}>
                  <div className="d-flex justify-content-between gap-3 px-3 py-2">
                    <span className="text-muted">Window</span>
                    <span className="fw-semibold text-end">{sessionWindow}</span>
                  </div>
                </div>
              )}

              {hasMonthly && (
                <MonthlyPlateMatchList matches={monthlyMatches} />
              )}

              {plateOverride == null && confidencePct != null && confidencePct < 75 && (
                <Alert variant="warning" className="mb-3 rounded-3 py-2 small">
                  Results may not be reliable — please review the plate manually before using.
                </Alert>
              )}

              <div className="d-flex gap-2 mt-3">
                <Button type="button" className="rounded-4 flex-fill" onClick={handleClose}>
                  Dismiss
                </Button>
                <Button type="button" className="rounded-4 btn-main flex-fill" onClick={handleRetry}>
                  
                  Scan again
                </Button>
              </div>
            </div>
          );
        })()}

        {/* ── Error stage ── */}
        {stage === "error" && (
          <>
            <Alert variant="warning" className="mb-3 rounded-3">
              {errorMessage}
            </Alert>
            <div className="d-flex justify-content-end gap-2">
              <Button type="button" className="rounded-4" onClick={handleClose}>
                Close
              </Button>
              <Button type="button" className="rounded-4 btn-main" onClick={handleRetry}>
                Try again
              </Button>
            </div>
          </>
        )}

      </Modal.Body>
    </Modal>
  );
}
